/**
 * src/ai/useAiChat.js
 *
 * Hook that manages the AI chat state and the communication with the backend.
 *
 * ── Conversation model ────────────────────────────────────────────────────
 *
 *  messages: [{ role: 'user'|'assistant', content: string }]
 *
 *  The full array is sent to the server on every turn (stateless backend).
 *  This mirrors the OpenAI Chat API and allows multi-turn conversations
 *  without server-side session storage.
 *
 * ── Usage ─────────────────────────────────────────────────────────────────
 *
 *  const { messages, loading, error, send, reset } = useAiChat({ screenId })
 *
 *  screenId: string built from the current module state, e.g. "fin-ban".
 *            Pass null/undefined when no module is active.
 *
 * ── Phase 3 note ──────────────────────────────────────────────────────────
 *
 *  When the server starts returning action results (tool calls), the response
 *  body will include an `actions` array.  Add handling here before pushing
 *  the assistant message to the messages array.
 */

import { useState, useCallback } from 'react'
import config from '../config.json'
import { useAiAssistant } from './AiAssistantContext'

// config.apiUrl is set at login (Login.jsx → completeLogin), not in config.json.
// Must be read at request time, not at module load — otherwise it stays undefined.
function getChatUrl() {
  if (!config.apiUrl) {
    throw new Error('apiUrl no configurada')
  }
  // apiUrl already includes /api (e.g. http://localhost:3000/api)
  return `${config.apiUrl}/ai/chat`
}

// Maximum messages to keep in history to avoid bloating the request.
// Older messages are trimmed from the top (oldest first).
// System messages are never in the array (handled server-side).
const MAX_HISTORY = 20

export function useAiChat({ screenId }) {
  const [messages, setMessages] = useState([])
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState(null)
  const { setEnabled } = useAiAssistant()

  /**
   * Send a new user message and append the assistant reply.
   *
   * @param {string} userText  The message typed by the user
   */
  const send = useCallback(async (userText) => {
    if (!userText || !userText.trim()) return

    const userMessage = { role: 'user', content: userText.trim() }

    // Optimistically show the user message right away
    const nextMessages = [...messages, userMessage].slice(-MAX_HISTORY)
    setMessages(nextMessages)
    setLoading(true)
    setError(null)

    try {
      const res = await fetch(getChatUrl(), {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          token:           config.token,
        },
        body: JSON.stringify({
          messages: nextMessages,
          screenId: screenId || null,
        }),
      })

      const data = await res.json()

      if (data.status !== 'ok') {
        setError(data.msg || 'Error al comunicarse con el asistente.')
        return
      }

      const assistantMessage = {
        role:    'assistant',
        content: data.body.content,
        // Expose ticket metadata to the UI (optional badge, etc.)
        ticketCreated: data.body.ticketCreated,
        ticketId:      data.body.ticketId,
      }

      setMessages(prev => [...prev, assistantMessage].slice(-MAX_HISTORY))

      if (typeof data.body.assistantEnabled === 'boolean') {
        if (data.body.assistantEnabled) {
          await setEnabled(true)
        } else {
          // Let the user read the confirmation before the widget hides.
          setTimeout(() => setEnabled(false), 2000)
        }
      }

    } catch (err) {
      setError(config.fetchErrorMsg || 'Error de conexión.')
    } finally {
      setLoading(false)
    }
  }, [messages, screenId, setEnabled])

  /** Clear the conversation (start fresh) */
  const reset = useCallback(() => {
    setMessages([])
    setError(null)
  }, [])

  return { messages, loading, error, send, reset }
}
